import { formatAlert } from '../../../utils/alertText';

/**
 * Group alerts by the account they are about.
 *
 * The dashboard rendered one card per finding. In production all eight
 * high-risk alerts belonged to `@KimDotcom`, so six cards described one
 * account's problem as six problems - and crowded out any second account that
 * might have appeared. Grouping is the difference between "six things are
 * wrong" and "one client needs a call".
 *
 * Accounts are ordered by severity first (an account with a critical finding
 * outranks one with more high findings), then by finding count, then by
 * recency - so the row you should act on first is the row at the top.
 */
export function groupAlertsByAccount(alerts = []) {
	const byAccount = new Map();

	for (const item of alerts) {
		const handle = item?.account?.handle;
		// An alert with no account cannot be acted on and must not become a
		// phantom "unknown" row; it is counted nowhere rather than shown wrong.
		if (!handle) continue;

		if (!byAccount.has(handle)) {
			byAccount.set(handle, {
				handle,
				clientName: item.client?.name || null,
				avatar: item.account?.avatar || null,
				platform: item.account?.platform || null,
				risk: null,
				critical: 0,
				high: 0,
				total: 0,
				newestAt: null,
				scanId: item.id || null,
				findings: [],
			});
		}

		const row = byAccount.get(handle);
		const severity = item.alert?.severity === 'critical' ? 'critical' : 'high';
		row[severity] += 1;
		row.total += 1;

		const at = item.alert?.timestamp || item.scanDate || null;
		if (at && (!row.newestAt || new Date(at) > new Date(row.newestAt))) {
			row.newestAt = at;
			// The scan to open is the one behind the most recent finding.
			row.scanId = item.id || row.scanId;
		}

		const { category, detail } = formatAlert(item.alert);
		row.findings.push({ severity, category, detail, at, scanId: item.id || null });
	}

	const rows = [...byAccount.values()];
	for (const row of rows) {
		row.findings.sort((a, b) => {
			if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
			return new Date(b.at || 0) - new Date(a.at || 0);
		});
	}

	rows.sort((a, b) => {
		if (a.critical !== b.critical) return b.critical - a.critical;
		if (a.total !== b.total) return b.total - a.total;
		return new Date(b.newestAt || 0) - new Date(a.newestAt || 0);
	});

	return rows;
}

/** The most recent finding across every account, for the verdict's dateline. */
export function newestAlertAt(alerts = []) {
	let newest = null;
	for (const item of alerts) {
		const at = item?.alert?.timestamp || item?.scanDate;
		if (at && (!newest || new Date(at) > new Date(newest))) newest = at;
	}
	return newest;
}
